// client/src/components/pages/LexiStar/StatusBoard/RunStrip.jsx
//
// A truck's day as one strip (LS-09 §5.24, FR-TRIP-021): each trip a card
// (sites, material, tonnes, stage, litres against allowance, flags), joined by
// turn-around blocks at the sites (offloading · waiting · loading minutes). The
// active trip is expanded; finished trips collapse to a line; totals on the
// right. `children` render at the end of the strip — the *Next load from here*
// slot. Used by the run-grouped status board, the run drawer and the plan page.

import React from 'react';
import { Link } from 'react-router-dom';
import { StagePill, ProvenanceMarker, OverConsumptionTag } from './atoms';
import { ACTIVE_STAGES } from './statusLanguage';
import { fmtL, fmtNum, fmtPct } from './format';

const circled = (n) => (n >= 1 && n <= 20 ? String.fromCharCode(0x2460 + n - 1) : `#${n}`);

const FINISHED = ['COMPLETED', 'RECONCILED', 'CLOSED', 'CANCELLED', 'ABORTED', 'EXCEPTION_HOLD'];

function TripCard({ trip, expanded }) {
  const flags = trip.flags || [];
  const litres = (
    <span className="inline-flex items-center gap-1 tabular-nums">
      {fmtL(trip.consumed_l)}
      <ProvenanceMarker provenance={trip.provenance} />
      <span className="text-slate-400">/ {fmtL(trip.budget_l)}</span>
      <OverConsumptionTag
        fuel={{
          consumed_l: trip.consumed_l,
          budget_l: trip.budget_l,
          provenance: trip.provenance,
          over_consumption: trip.over_consumption,
        }}
      />
    </span>
  );

  if (!expanded) {
    return (
      <li
        className="flex-shrink-0 min-w-[190px] px-3 py-2 bg-surface border border-slate-200 rounded-lg text-xs text-slate-600"
        aria-label={`Trip ${trip.seq}: ${trip.from_name} to ${trip.to_name}`}
      >
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-800">{circled(trip.seq)}</span>
          <Link to={`/lexistar/trips/${trip.id}`} className="font-mono hover:underline">
            {trip.reference}
          </Link>
          <StagePill stage={trip.stage} />
        </div>
        <div className="mt-1 truncate">
          {trip.from_name} → {trip.to_name} · {litres}
        </div>
      </li>
    );
  }

  return (
    <li
      className={`flex-shrink-0 min-w-[240px] p-3 bg-surface rounded-lg text-xs text-slate-700 border ${trip.origin === 'prompted' ? 'border-dashed border-slate-400' : 'border-primary-300 shadow-sm'}`}
      aria-label={`Trip ${trip.seq}: ${trip.from_name} to ${trip.to_name}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-900">
          {circled(trip.seq)} {trip.from_name} → {trip.to_name}
        </span>
        <StagePill stage={trip.stage} />
      </div>
      <div className="mt-1 text-slate-500">
        <Link to={`/lexistar/trips/${trip.id}`} className="font-mono hover:underline">
          {trip.reference}
        </Link>
        {trip.material ? ` · ${trip.material}` : ''}
        {trip.planned_tonnes != null ? ` · ${fmtNum(trip.planned_tonnes, 1)} t` : ''}
        {trip.origin === 'prompted' && ' · retrospective'}
      </div>
      <div className="mt-2">{litres}</div>
      {flags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {flags.map((f) => (
            <span key={f} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">
              {f.replace(/_/g, ' ').toLowerCase()}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

function Turnaround({ block }) {
  const parts = [
    ['offloading', block.offloading_min],
    ['waiting', block.waiting_min],
    ['loading', block.loading_min],
  ].filter(([, v]) => v !== null && v !== undefined);
  return (
    <li
      className="flex-shrink-0 self-center px-2 py-1 rounded bg-slate-100 text-[10px] text-slate-600 text-center"
      aria-label={`Turn-around at ${block.site_name}`}
    >
      <div className="font-semibold">{block.site_name}</div>
      {parts.map(([k, v]) => `${k} ${Math.round(v)}m`).join(' · ') || 'turn-around'}
    </li>
  );
}

export default function RunStrip({ summary, children }) {
  if (!summary) return null;
  const trips = [...(summary.trips || [])].sort((a, b) => a.seq - b.seq);
  const turnarounds = summary.turnarounds || [];
  const activeId =
    trips.find((t) => ACTIVE_STAGES.includes(t.stage))?.id ||
    trips.find((t) => !FINISHED.includes(t.stage))?.id;
  const totals = summary.totals || {};

  const items = [];
  trips.forEach((t, i) => {
    items.push(
      <TripCard key={t.id} trip={t} expanded={t.id === activeId || !FINISHED.includes(t.stage)} />
    );
    const ta = turnarounds.find((b) => b.after_seq === t.seq);
    if (ta && i < trips.length - 1) items.push(<Turnaround key={`ta-${t.seq}`} block={ta} />);
  });

  return (
    <div className="flex flex-col lg:flex-row gap-3">
      <ol className="flex-1 min-w-0 flex items-stretch gap-2 overflow-x-auto pb-1">
        {items}
        {children}
      </ol>
      <dl className="flex-shrink-0 grid grid-cols-3 lg:grid-cols-2 gap-x-4 gap-y-1 text-xs lg:w-56 p-3 bg-slate-50 border border-slate-200 rounded-lg">
        <dt className="text-slate-500">Loads</dt>
        <dd className="tabular-nums text-slate-900 font-semibold">{fmtNum(totals.loads)}</dd>
        <dt className="text-slate-500">Tonnes</dt>
        <dd className="tabular-nums text-slate-900">{fmtNum(totals.tonnes, 1)}</dd>
        <dt className="text-slate-500">Loaded km</dt>
        <dd className="tabular-nums text-slate-900">{fmtNum(totals.loaded_km)}</dd>
        <dt className="text-slate-500">Empty km</dt>
        <dd className="tabular-nums text-slate-900">
          {fmtNum(totals.empty_km)}
          {totals.empty_running_pct != null && (
            <span className="text-slate-500"> ({fmtPct(totals.empty_running_pct)})</span>
          )}
        </dd>
        <dt className="text-slate-500">Litres</dt>
        <dd
          className={`tabular-nums ${Number(totals.litres_used) > Number(totals.litres_allowance) ? 'text-amber-700 font-semibold' : 'text-slate-900'}`}
        >
          {fmtNum(totals.litres_used)} / {fmtL(totals.litres_allowance)}
        </dd>
      </dl>
    </div>
  );
}

export { circled };
